<template>
  <div class="draft-composer" @keydown.enter="onEnter">
    <div v-if="modelValue.length" class="drug-list mb-4">
      <div v-for="(item, index) in modelValue" :key="item._key || index" class="drug-item-block">
        <div class="drug-item">
          <span class="drug-item-index">{{ index + 1 }}</span>
          <div class="drug-item-body">
            <div class="drug-item-top">
              <div class="drug-item-identity">
                <span class="drug-item-name">{{ item.drug_name }}</span>
                <span v-if="item.item_kind === 'compounded'" class="drug-item-presentation">Fórmula</span>
                <span v-if="presentationSummary(item)" class="drug-item-presentation">
                  {{ presentationSummary(item) }}
                </span>
                <RegulatoryBadge v-if="item.regulatory_type" :classification="badgeFor(item)"/>
              </div>
              <div class="drug-item-aside">
                <span v-if="adminName(item)" class="drug-item-route">{{ adminName(item) }}</span>
                <button type="button" class="btn btn-icon btn-sm btn-bg-light btn-active-color-primary"
                        title="Excluir medicamento" @click="remove(index)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <div v-if="item.dosage_desc" class="drug-item-dosage">{{ item.dosage_desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="drug-form">
      <PrescriptionDrugFields :drug="draft"
                              agenda-style
                              :drug-admins="drugAdmins"
                              :errors="errors"
                              :id-prefix="idPrefix"
                              @catalog="applyCatalog"
                              @clear-catalog="clearCatalog"/>
      <div class="draft-composer-actions">
        <button type="button" class="btn btn-light-primary" @click="addItem">
          <i class="bi bi-plus-lg"></i>
          {{ draft.item_kind === 'compounded' ? 'Adicionar fórmula' : 'Adicionar medicamento' }}
        </button>
        <small v-if="errors.form" class="text-danger">{{ errors.form }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from "@/helpers/alert";
import {classificationFromType} from "@/helpers/regulatoryTypes";
import PrescriptionDrugFields from "./PrescriptionDrugFields.vue";
import RegulatoryBadge from "./RegulatoryBadge.vue";

export default {
  name: "DraftDrugComposer",
  components: {PrescriptionDrugFields, RegulatoryBadge},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    drugAdmins: {
      type: Array,
      default: () => [],
    },
    regulatoryTypes: {
      type: Array,
      default: () => [],
    },
    idPrefix: {
      type: String,
      default: 'draft-drug',
    },
  },
  data() {
    return {
      draft: this.emptyDraft(),
      errors: {},
      seq: 0,
    };
  },
  watch: {
    drugAdmins: {
      immediate: true,
      handler() {
        if (!this.draft.id_drug_admin) {
          this.draft.id_drug_admin = this.defaultAdminId();
        }
      },
    },
  },
  methods: {
    emptyDraft() {
      return {
        item_kind: 'medication',
        drug_name: '',
        id_drug: null,
        concentration: '',
        pharmaceutical_form: '',
        quantity: '',
        measure: '',
        composition: '',
        continuous_use: false,
        monthly_quantity: '',
        dosage_desc: '',
        treatment_time: '',
        drug_obs: '',
        id_drug_admin: this.defaultAdminId(),
        controlled: false,
        regulatory_type: null,
        regulatory_type_description: null,
      };
    },
    defaultAdminId() {
      const oral = this.drugAdmins.find((admin) => /oral/i.test(admin.drug_admin_name ?? ''));
      return oral?.id_drug_admin ?? this.drugAdmins[0]?.id_drug_admin ?? '';
    },
    applyCatalog(drug) {
      this.draft.id_drug = drug.id_drug;
      this.draft.drug_name = drug.drug_name;
      this.draft.concentration = drug.concentration ?? '';
      this.draft.pharmaceutical_form = drug.pharmaceutical_form ?? '';
      this.draft.controlled = !!drug.controlled;
      this.draft.regulatory_type = drug.regulatory_type ?? null;
      this.draft.regulatory_type_description = drug.regulatory_type_description ?? null;
    },
    clearCatalog() {
      this.draft.id_drug = null;
      this.draft.controlled = false;
      if (this.draft.item_kind === 'compounded') {
        return;
      }
      this.draft.regulatory_type = null;
      this.draft.regulatory_type_description = null;
    },
    presentationSummary(item) {
      if (item.item_kind === 'compounded') {
        return [item.pharmaceutical_form, [item.quantity, item.measure].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(' · ');
      }
      return [item.concentration, item.pharmaceutical_form, item.quantity, item.treatment_time]
          .filter(Boolean)
          .join(' · ');
    },
    adminName(item) {
      return this.drugAdmins.find((admin) => admin.id_drug_admin === item.id_drug_admin)?.drug_admin_name ?? '';
    },
    badgeFor(item) {
      return classificationFromType(item.regulatory_type);
    },
    validate() {
      const errors = {};
      const nome = (this.draft.drug_name ?? '').trim();
      const posologia = (this.draft.dosage_desc ?? '').trim();
      const formula = this.draft.item_kind === 'compounded';

      if (nome.length < 3) {
        errors.drug_name = [formula ? 'Informe o nome da fórmula.' : 'Informe o medicamento.'];
      }
      if (posologia.length < 3) {
        errors.dosage_desc = ['Informe a posologia.'];
      }
      if (formula) {
        if (!(this.draft.pharmaceutical_form ?? '').trim()) {
          errors.pharmaceutical_form = ['Selecione a forma física.'];
        }
        if (!(this.draft.quantity ?? '').trim()) {
          errors.quantity = ['Informe a quantidade.'];
        }
        if (!(this.draft.measure ?? '').trim()) {
          errors.measure = ['Selecione a medida.'];
        }
        if ((this.draft.composition ?? '').trim().length < 3) {
          errors.composition = ['Informe a composição.'];
        }
        if (!this.draft.regulatory_type) {
          errors.regulatory_type = ['Selecione o tipo de receituário.'];
        }
      } else if (!this.draft.id_drug_admin) {
        errors.id_drug_admin = ['Selecione a via de administração.'];
      }
      if (!formula && this.draft.controlled && !(this.draft.quantity ?? '').trim()) {
        errors.form = 'Quantidade é obrigatória para medicamento controlado.';
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    addItem() {
      if (!this.validate()) {
        if (this.errors.form) {
          Alert.toastError(this.errors.form);
        }
        return false;
      }

      const item = {
        ...this.draft,
        drug_name: this.draft.drug_name.trim(),
        dosage_desc: this.draft.dosage_desc.trim(),
        _key: `draft-${++this.seq}`,
      };

      this.$emit('update:modelValue', [...this.modelValue, item]);
      this.draft = this.emptyDraft();
      this.errors = {};
      return true;
    },
    remove(index) {
      const next = this.modelValue.slice();
      next.splice(index, 1);
      this.$emit('update:modelValue', next);
    },
    /**
     * Usado no Cadastrar: se o médico preencheu o último item e não clicou
     * em Adicionar, inclui na lista. Vazio é ok; inválido trava o submit.
     */
    commitDraft() {
      const nome = (this.draft.drug_name ?? '').trim();
      const posologia = (this.draft.dosage_desc ?? '').trim();
      const composition = (this.draft.composition ?? '').trim();
      if (!nome && !posologia && !composition) {
        this.errors = {};
        return 'empty';
      }

      return this.addItem() ? 'added' : 'invalid';
    },
    reset() {
      this.draft = this.emptyDraft();
      this.errors = {};
    },
    onEnter(event) {
      if (event.target?.tagName === 'TEXTAREA' || event.target?.closest?.('.drug-search, .ubbi-select')) {
        return;
      }
      event.preventDefault();
      this.addItem();
    },
  },
};
</script>

<style scoped>
.drug-form {
  padding: 0;
}

.drug-form :deep(.form-label) {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4b5675;
  margin-bottom: 0.35rem;
}

.draft-composer-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.drug-list {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.drug-item-block + .drug-item-block {
  border-top: 1px solid #eef0f2;
}

.drug-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
}

.drug-item-index {
  flex-shrink: 0;
  width: 18px;
  padding-top: 2px;
  color: #6c757d;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  text-align: right;
}

.drug-item-body {
  flex: 1;
  min-width: 0;
}

.drug-item-top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.drug-item-identity {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  min-width: 0;
  flex: 1 1 160px;
}

.drug-item-name {
  font-weight: 600;
  color: #212529;
  line-height: 1.35;
}

.drug-item-presentation {
  color: #6c757d;
  font-size: 0.8rem;
}

.drug-item-aside {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.drug-item-route {
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f3f5;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 600;
}

.drug-item-dosage {
  margin-top: 4px;
  color: #495057;
  font-size: 0.875rem;
}
</style>
